import { Loader2, AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/Button/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../lib/ui/dialog';

export interface DeleteDialogProps {
  /** Whether the dialog is currently visible. Controlled by the parent. */
  open: boolean;
  /** Toggle handler — invoked when the dialog wants to close (e.g. cancel). */
  onOpenChange: (open: boolean) => void;
  /**
   * Snapshot of the active cache name at dialog-open time (R-001). The parent
   * captures it via `openDeleteDialog` and freezes it for the lifetime of the
   * dialog so a concurrent rename does not change the displayed name.
   */
  cacheName: string;
  /**
   * Confirm handler — runs the delete mutation in the parent hook. The dialog
   * does NOT close itself on confirm; the parent dismisses it as part of the
   * mutation's success / failure branches.
   */
  onConfirm: () => Promise<void>;
  /** True while any mutation is in-flight — disables buttons, shows spinner. */
  isMutating: boolean;
}

export function DeleteDialog({
  open,
  onOpenChange,
  cacheName,
  onConfirm,
  isMutating,
}: DeleteDialogProps) {
  const { t } = useTranslation('form-cache-selector');

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                <AlertTriangle
                  data-testid="alert-triangle-icon"
                  className="h-5 w-5 text-amber-600"
                />
              </div>
              <DialogTitle>{t('deleteDialog.title')}</DialogTitle>
            </div>
            <DialogClose data-testid="delete-dialog-close">
              <X className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">{t('deleteDialog.closeButton')}</span>
            </DialogClose>
          </div>
        </DialogHeader>
        <div className="mt-4 space-y-1">
          <p data-testid="delete-confirm-text" className="text-sm text-neutral-500">
            {t('deleteDialog.confirm', { name: cacheName })}
          </p>
          <p data-testid="delete-warning-text" className="text-sm text-neutral-500">
            {t('deleteDialog.warning')}
          </p>
        </div>
        <DialogFooter>
          <Button
            htmlType="button"
            type="outline"
            size="sm"
            autoFocus
            data-testid="delete-dialog-cancel"
            onClick={() => onOpenChange(false)}
            disabled={isMutating}
          >
            {t('deleteDialog.cancel')}
          </Button>
          <Button
            htmlType="button"
            type="plain"
            size="sm"
            style={{ backgroundColor: 'var(--color-error-border)', color: 'white' }}
            data-testid="delete-dialog-confirm"
            onClick={() => { void handleConfirm(); }}
            disabled={isMutating}
            leftIcon={
              isMutating ? (
                <Loader2 className="h-4 w-4 animate-spin" data-testid="delete-spinner" />
              ) : undefined
            }
          >
            {t('deleteDialog.confirmButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
